/**
 * @file
 * Tab List behavior.
 *
 * Handles tab activation, keyboard navigation, URL state management,
 * and coordination with tab-content components.
 *
 * Features:
 * - Click/keyboard activation of tabs
 * - Arrow key navigation with wrap-around
 * - URL params sync (?tabListId=tabId)
 * - Roving tabindex for accessibility
 * - Custom event dispatch for tab changes
 */

((Drupal, once) => {
  'use strict';

  /**
   * Custom event dispatched when a tab is activated.
   */
  interface TabChangeEvent extends CustomEvent {
    detail: {
      tabListId: string;
      tabId: string;
      previousTabId: string | null;
    };
  }

  /**
   * Get the active tab ID from URL params.
   */
  const getTabIdFromUrl = (tabListId: string): string | null => {
    const params = new URLSearchParams(window.location.search);
    return params.get(tabListId);
  };

  /**
   * Update URL params with the active tab ID.
   */
  const updateUrlWithTabId = (tabListId: string, tabId: string): void => {
    const url = new URL(window.location.href);
    url.searchParams.set(tabListId, tabId);
    window.history.replaceState({}, '', url.toString());
  };

  /**
   * Activate a tab and its corresponding panel.
   */
  const activateTab = (
    tabList: HTMLElement,
    tabButton: HTMLButtonElement,
    context: Document | HTMLElement,
    updateUrl: boolean = true
  ): void => {
    const tabListId = tabList.dataset.tabListId;
    const tabId = tabButton.dataset.tabId;

    if (!tabListId || !tabId) {
      return;
    }

    // Get current active tab for event
    const currentActive = tabList.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]');
    const previousTabId = currentActive?.dataset.tabId || null;

    // Skip if already active
    if (previousTabId === tabId) {
      return;
    }

    // Update all tabs in this list
    const allTabs = tabList.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    allTabs.forEach((tab) => {
      const isTargetTab = tab.dataset.tabId === tabId;
      tab.setAttribute('aria-selected', String(isTargetTab));
      tab.setAttribute('tabindex', isTargetTab ? '0' : '-1');
      tab.classList.toggle('tp-tab-list__tab--active', isTargetTab);
    });

    // Update data attribute on container
    tabList.dataset.activeTabId = tabId;

    // Find and show/hide corresponding panels using context
    const panels = context.querySelectorAll<HTMLElement>(`[data-tab-list-id="${tabListId}"][role="tabpanel"]`);
    panels.forEach((panel) => {
      const isPanelActive = panel.dataset.tabId === tabId;
      panel.hidden = !isPanelActive;
      panel.classList.toggle('tp-tab-content--hidden', !isPanelActive);
    });

    // Update URL
    if (updateUrl) {
      updateUrlWithTabId(tabListId, tabId);
    }

    // Dispatch custom event
    const event: TabChangeEvent = new CustomEvent('tabchange', {
      bubbles: true,
      detail: {
        tabListId,
        tabId,
        previousTabId,
      },
    });
    tabList.dispatchEvent(event);
  };

  /**
   * Handle keyboard navigation within tab list.
   */
  const handleKeydown = (
    event: KeyboardEvent,
    tabList: HTMLElement,
    tabs: HTMLButtonElement[],
    context: Document | HTMLElement
  ): void => {
    const currentTab = event.target as HTMLButtonElement;
    const currentIndex = tabs.indexOf(currentTab);

    if (currentIndex === -1) {
      return;
    }

    let newIndex: number | null = null;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        // Wrap to end if at beginning
        newIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
        break;

      case 'ArrowRight':
        event.preventDefault();
        // Wrap to beginning if at end
        newIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
        break;

      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;

      case 'End':
        event.preventDefault();
        newIndex = tabs.length - 1;
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        activateTab(tabList, currentTab, context);
        return;
    }

    if (newIndex !== null && tabs[newIndex]) {
      tabs[newIndex].focus();
      // Activate on focus (follows ARIA Authoring Practices recommendation)
      activateTab(tabList, tabs[newIndex], context);
    }
  };

  Drupal.behaviors.tpTabList = {
    attach(context: Document | HTMLElement) {
      const tabLists = once('tp-tab-list', '[role="tablist"][data-tab-list-id]', context);

      tabLists.forEach((element) => {
        const tabList = element as HTMLElement;
        const tabListId = tabList.dataset.tabListId;

        if (!tabListId) {
          return;
        }

        const tabs = Array.from(tabList.querySelectorAll<HTMLButtonElement>('[role="tab"]'));

        if (tabs.length === 0) {
          return;
        }

        // Check URL for initial active tab
        const urlTabId = getTabIdFromUrl(tabListId);
        if (urlTabId) {
          const urlTab = tabs.find((tab) => tab.dataset.tabId === urlTabId);
          if (urlTab) {
            activateTab(tabList, urlTab, context, false);
          }
        }

        // Add click handlers
        tabs.forEach((tab) => {
          tab.addEventListener('click', (event) => {
            event.preventDefault();
            activateTab(tabList, tab, context);
            tab.focus();
          });
        });

        // Add keyboard navigation
        tabList.addEventListener('keydown', (event) => {
          handleKeydown(event as KeyboardEvent, tabList, tabs, context);
        });
      });
    },
  };
})(Drupal, once);
